import i18n from './i18n';
import {
  AdyenConfigType,
  PaymentMethodOption,
} from '@/contexts/payments-context';
import { MICRO_CONVERSION, roundMicrosToNearestWholeCent } from '@/utils/money';
import { PaymentMethod, PaymentMethodType, PaymentsStatus } from '@/enums';
import { Platform } from 'react-native';
import type {
  AddressData,
  BrowserInfo,
  OnChangeData,
  PaymentData,
} from '@adyen/adyen-web';
import type { TransactionFeeMap } from '@/utils/transaction-fees';

export type AdyenPaymentMethodType =
  | 'ach'
  | 'debit'
  | 'eft_directdebit_CA'
  | 'klarna'
  | 'klarna_account'
  | 'payto'
  | 'plaid_ach'
  | 'scheme';

export interface AdyenDropinOnSubmitData {
  data: {
    browserInfo?: BrowserInfo;
    checkoutAttemptId?: string;
    clientStateDataIndicator: boolean;
    paymentMethod: {
      encryptedCardNumber: string;
      type: AdyenPaymentMethodType;
    };
    riskData?: PaymentData['riskData'];
    storePaymentMethod?: boolean;
  };
  isValid: boolean;
}
export interface AdyenDropinOnChangeData {
  data: {
    billingAddress?: AddressData;
    browserInfo?: BrowserInfo;
    clientStateDataIndicator: boolean;
    paymentMethod: {
      encryptedCardNumber: string;
      holderName: string;
      type: 'scheme' | 'ach';
    };
    riskData?: PaymentData['riskData'];
  };
  errors?: OnChangeData['errors'];
  isValid: boolean;
  valid?: OnChangeData['valid'];
}

export const ADD_PAYMENT_METHOD_KEY = 'add-payment-method';

export const ADYEN_TYPE_TO_PAYMENT_METHOD_TYPE_MAP: Record<
  AdyenPaymentMethodType,
  PaymentMethodType
> = {
  ach: PaymentMethodType.ACH,
  debit: PaymentMethodType.DEBIT_CARD,
  eft_directdebit_CA: PaymentMethodType.PAD,
  klarna: PaymentMethodType.KLARNA,
  klarna_account: PaymentMethodType.KLARNA_ACCOUNT,
  payto: PaymentMethodType.PAYTO,
  plaid_ach: PaymentMethodType.PLAID_ACH,
  scheme: PaymentMethodType.CREDIT_CARD,
};

export const PAYMENT_METHOD_TYPE_TO_ADYEN_TYPE_MAP: Record<
  PaymentMethodType,
  AdyenPaymentMethodType
> = {
  [PaymentMethodType.ACH]: 'ach',
  [PaymentMethodType.CREDIT_CARD]: 'scheme',
  [PaymentMethodType.DEBIT_CARD]: 'scheme',
  [PaymentMethodType.KLARNA]: 'klarna',
  [PaymentMethodType.KLARNA_ACCOUNT]: 'klarna_account',
  [PaymentMethodType.PAD]: 'eft_directdebit_CA',
  [PaymentMethodType.PAYTO]: 'payto',
  [PaymentMethodType.PLAID_ACH]: 'plaid_ach',
  [PaymentMethodType.TAB]: 'scheme',
};

interface PaymentAtOrderPlacementSettings {
  payUponOrdering: boolean;
  requirePaymentMethodOnOrderPlacement: boolean;
  supplierPaymentsStatus: PaymentsStatus;
}

// Tells you whether a restaurant has to supply a card to place an order.
export const requiresPaymentAtOrderPlacement = ({
  payUponOrdering,
  requirePaymentMethodOnOrderPlacement,
  supplierPaymentsStatus,
}: PaymentAtOrderPlacementSettings) =>
  supplierPaymentsStatus === PaymentsStatus.Enabled &&
  (payUponOrdering || requirePaymentMethodOnOrderPlacement);

export const calculateTransactionFeeInMicros = (
  feeMap: TransactionFeeMap,
  methodType: PaymentMethodType,
  amountInMicros: number,
  isGrossUp: boolean,
): number => {
  const flat = feeMap.FLAT[methodType];
  const pct = feeMap.PERCENTAGE[methodType];
  if (flat) {
    return flat * MICRO_CONVERSION;
  }
  if (pct) {
    const p = pct / 100;
    const multiplier = isGrossUp ? p / (1 - p) : p;
    return roundMicrosToNearestWholeCent(multiplier * amountInMicros);
  }
  return 0;
};

export const toAdyenPaymentJson = (option: PaymentMethodOption) => {
  return JSON.stringify({
    data: {
      paymentMethod: {
        storedPaymentMethodId: option.adyenPaymentID,
        type: PAYMENT_METHOD_TYPE_TO_ADYEN_TYPE_MAP[option.type],
      },
    },
  });
};

export const toUnsavedPaymentMethodOption = (
  adyenData: AdyenDropinOnSubmitData,
): PaymentMethodOption => {
  // Payment method type will never be PLAID_ACH
  const paymentMethodType = adyenData.data.paymentMethod.type;

  if (paymentMethodType === 'debit') {
    adyenData.data.paymentMethod.type = 'scheme';
  }

  return {
    adyenPaymentID: 'UNSAVED_ID',
    adyenPaymentMethodJSON: JSON.stringify(adyenData),
    displayText: i18n.t(`payment_method.${paymentMethodType}`),
    isDefault: false,
    isUnsaved: true,
    subtype: 'MCCOMMERCIALCREDIT',
    type: ADYEN_TYPE_TO_PAYMENT_METHOD_TYPE_MAP[paymentMethodType],
    uuid: 'UNSAVED_PAYMENT_METHOD_UUID',
  };
};

/**
 * Parses the PaymentMethod from a PaymentMethodOption
 * @param {PaymentMethodOption | null | undefined} paymentMethodOption Payment method option returned from the server
 * @returns {PaymentMethod} the parsed PaymentMethod
 */
export const getPaymentMethodFromPaymentMethodOption = (
  paymentMethodOption?: PaymentMethodOption | null,
): PaymentMethod => {
  switch (paymentMethodOption?.type) {
    case PaymentMethodType.ACH:
      return PaymentMethod.ACH;
    case PaymentMethodType.PLAID_ACH:
      return PaymentMethod.PLAID_ACH;
    case PaymentMethodType.PAD:
      return PaymentMethod.PAD;
    case PaymentMethodType.PAYTO:
      return PaymentMethod.PAYTO;
    case PaymentMethodType.TAB:
      return PaymentMethod.TAB;
    case PaymentMethodType.CREDIT_CARD: {
      const [paymentMethodType] = paymentMethodOption!.displayText.split(' ');
      return paymentMethodType.toUpperCase() as PaymentMethod;
    }
    default:
      return PaymentMethod.UNKNOWN;
  }
};

/*
  Since the injected JS script requires the AdyenCheckout to be
  instantiated with object properties, we cannot use JSON.parse
  since it does not quote string values. We also cannot use the
  stringified JSON from the hasura action since keys are double
  quoted.

  This regex replace will return a string that does not quote
  object keys but maintain quotes around string object values.
*/
export const toNonQuotedJSONString = (jsonString: string) =>
  jsonString.replace(/"(\w+)"\s*:/g, '$1:');

// All string values must be single quoted to represent JS strings
export const getInjectedJS = (
  config: AdyenConfigType,
  requireSavedPaymentMethod: boolean,
) => `
  (async () => {
    const { AdyenCheckout, Dropin } = window.AdyenWeb;

    const checkout = await AdyenCheckout({
      clientKey: '${config.clientKey}',
      countryCode: '${config.countryCode}',
      environment: '${config.environment}',
      locale: '${i18n.locale}',
      onChange: state => {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: "CHANGE",
          state,
        }));
      },
      onSubmit: state => {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: "SUBMIT",
          state,
        }));
      },
      paymentMethodsResponse: ${toNonQuotedJSONString(
        config.paymentMethodsJson,
      )},
      showPayButton: false,
    });
    const dropinComponent = new Dropin(checkout, {
      openFirstPaymentMethod: false,
      paymentMethodsConfiguration: {
        ach: {
          name: '${i18n.t('payment_method.ach.name')}',
        },
        card: {
          billingAddressRequired: true,
          enableStoreDetails: ${!requireSavedPaymentMethod},
          hasHolderName: true,
          holderNameRequired: true,
          name: '${i18n.t('payment_method.card.name')}',
        },
      },
      showRadioButton: true,
      showStoredPaymentMethods: false,
    }).mount('#dropin-container');

    // https://github.com/react-native-webview/react-native-webview/issues/356
    const webviewTarget = ${Platform.OS === 'ios' ? 'window' : 'document'};

    webviewTarget.addEventListener('message', event => {
      /*
        WebView emits a few message events during initialization but
        we only match on the postMessage key from the continue button
        press to prevent false dropin submissions.
      */

      if (event.data === '${ADD_PAYMENT_METHOD_KEY}') {
        dropinComponent.submit();
      }
    });
  })();
  `;

export const getWebViewTemplate = (injectedJS: string) => `
  <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <link rel="stylesheet"
          href="https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/6.43.0/adyen.css"
          integrity="sha384-PWrMXiOTu6vDvUL+llWHVeWnBIxMTJ6PxGu6f8gKPGpoklzxJxRmeuOgVHE8Xk5U"
          crossorigin="anonymous">
        <style>${dropInStyles}</style>
      </head>
      <body>
        <div id="dropin-container"></div>
        <script src="https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/6.43.0/adyen.js"
          integrity="sha384-vGMznmDxVQazPYlqNCYscSGkdlWa5TiRGf2OL0dKMT54mXx78IMn1lw070DmYGzm"
          crossorigin="anonymous">
        </script>
        <script>${injectedJS}</script>
      </body>
    </html>
  `;

export const dropInStyles = `
  .adyen-checkout__dropin {
    font-family: Geist, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  }

  .adyen-checkout__payment-method {
    background-color: #fff;
  }

  .adyen-checkout__payment-method {
    border: none;
  }

  .adyen-checkout__payment-method--selected {
    border: none;
  }

  .adyen-checkout__payment-method__radio--selected {
    background-color: #363840;
  }

  .adyen-checkout__payment-method__name {
    color: #363840;
  }

  .adyen-checkout__button {
    background-color: #363840;
    border-radius: 4px;
  }

  .adyen-checkout__input {
    caret-color: #363840;
  }

  .adyen-checkout__dropdown__button {
    box-shadow: none;
    border: 1px solid #363840;
  }

  .adyen-checkout__input:active, .adyen-checkout__input:active:hover, .adyen-checkout__input:focus, .adyen-checkout__input:focus:hover {
    box-shadow: none;
    border: 1px solid #363840;
  }

  /* this does not work */
  .adyen-checkout__dropdown__button--invalid {
    border-color: #dc1414;
  }

  .adyen-checkout__dropdown__button {
    border: 1px solid #b9c4c9;
  }

  .adyen-checkout__dropdown__list {
    max-height: 172px;
  }

  .adyen-checkout__dropdown__list.adyen-checkout__dropdown__list--active {
    margin-top: 8px;
  }

  .adyen-checkout__dropdown__button--active, .adyen-checkout__dropdown__button--active:hover, .adyen-checkout__dropdown__button:active, .adyen-checkout__dropdown__button:focus {
    box-shadow: none;
    border: 1px solid #363840;
  }

  .adyen-checkout__dropdown__element.adyen-checkout__dropdown__element--active {
    background: #C0F7C2;
  }

  .adyen-checkout__dropdown__element:active, .adyen-checkout__dropdown__element:focus, .adyen-checkout__dropdown__element:hover {
    background: #C0F7C2;
  }

  .adyen-checkout__dropdown__element.adyen-checkout__dropdown__element--active:active, .adyen-checkout__dropdown__element.adyen-checkout__dropdown__element--active:focus, .adyen-checkout__dropdown__element.adyen-checkout__dropdown__element--active:hover {
    background: #C0F7C2;
  }

  .adyen-checkout__label--focused .adyen-checkout__label__text {
    color: #363840;
  }

  .adyen-checkout__label__text {
    color: #363840;
  }

  .adyen-checkout__input--error {
    border-color: #dc1414;
    color: #dc1414;
  }

  .adyen-checkout__label__text--error, .adyen-checkout-contextual-text--error {
    color: #dc1414;
  }

  .adyen-checkout__input--valid {
    border-bottom-color: #79D97C;
  }

  .adyen-checkout__checkbox__input:checked+.adyen-checkout__checkbox__label:after {
    background-color: #363840;
    border: 1px solid #363840;
  }

  .adyen-checkout__spinner {
    border: 4px solid #e2e2e2;
    border-top-color: #79D97C;
  }

  .adyen-checkout__payment-method__radio {
    border: 1px solid #363840;
    width: 16px;
    height: 16px;
    left: 16px;
  }

  .adyen-checkout__payment-method__header {
    padding-top: 8px;
  }

  .adyen-checkout__payment-method__radio--selected {
    background-color: #fff;
  }

  .adyen-checkout__payment-method__radio:after {
    background-color: #363840;
    width: 8px;
    height: 8px;
  }
`;
